import { Container, Particle, Texture } from "pixi.js";
import { ParticleLayer } from "../particle-layer";
import {
  createHalo,
  disposeHalo,
  drawHalo,
  hideHalo,
  type HaloState as Halo,
} from "./halo";
import type { ActivePress, NoteEffect, NoteEffectFrame } from "./types";

// ── Halo ──
const HALO_BASE_RADIUS = 15;

// ── Particles ──
const PARTICLE_EMIT_INTERVAL_MS = 18;
const PARTICLE_LIFETIME_MS = 1400;
const PARTICLE_INITIAL_VY = -240; // px/sec, upward (ejection at the note base)
const PARTICLE_VY_SPREAD = 85;
const PARTICLE_VX_SPREAD = 100;
/**
 * Downward acceleration in px/sec². Tune this to control how quickly particles
 * slow, stall, and fall back down.
 */
const PARTICLE_GRAVITY = 200;
const PARTICLE_MIN_SIZE = 1.8;
const PARTICLE_MAX_SIZE = 3.2;
/** Reference speed (px/sec) used to normalize the stretch effect. */
const PARTICLE_REF_SPEED = 320;
/** How much a fast particle is stretched along its velocity direction (× base size). */
const PARTICLE_STRETCH = 5.5;
/** Thickness of the streak relative to base size (perpendicular to velocity). */
const PARTICLE_THICKNESS_FACTOR = 0.7;
/** Bias the particle tint toward white so they stay visible over saturated notes. */
const PARTICLE_WHITE_MIX = 0.55;
const MAX_PARTICLES = 1000;
/**
 * Les bornes de l'horloge du rouleau, telle que la gerbe la suit.
 *
 * En deçà de la moitié, les étincelles paraissent suspendues : leur durée de
 * vie dépasse trois secondes et la gerbe cesse de jaillir pour flotter. Au-delà
 * du double, elle ne se lit plus que comme un éclair. Entre les deux, le
 * rapport passe tel quel.
 */
const CLOCK_MIN = 0.5;
const CLOCK_MAX = 2;

/** Mix a 0xRRGGBB color toward white (factor 0 = unchanged, 1 = pure white). */
function brightenTowardWhite(color: number, factor: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const nr = Math.round(r + (255 - r) * factor);
  const ng = Math.round(g + (255 - g) * factor);
  const nb = Math.round(b + (255 - b) * factor);

  return (nr << 16) | (ng << 8) | nb;
}

/**
 * Map a 0–1 note-on velocity to an intensity multiplier. The effect should
 * still be visible for soft notes, so we floor it at 0.5 instead of 0.
 */
function velocityIntensity(velocity: number): number {
  const v = Math.max(0, Math.min(1, velocity));

  return 0.5 + v * 0.7;
}

type HaloState = Halo & {
  emitAccumulatorMS: number;
};

type ParticleState = {
  active: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  ageMS: number;
  baseSize: number;
  particle: Particle;
};

/**
 * Halo + upward particle burst at the playhead for each strike crossing it.
 *
 * Particles are rendered through a `ParticleContainer` for batched GPU draws.
 * This is much friendlier to weaker mobile GPUs (iPad, low-end Android) than
 * a per-particle Sprite scene-graph would be.
 *
 * # L'horloge du rouleau
 *
 * La gerbe ne vit pas à la montre mais à la vitesse du rouleau
 * (`rollSpeedRatio`) : sous des notes qui filent, des étincelles à vitesse
 * fixe paraissent molles, détachées de ce qui les projette.
 *
 * Toute l'animation est pressée, et pas seulement l'élan initial : la gravité,
 * le vieillissement et la cadence d'émission passent par le même facteur. La
 * gerbe garde donc exactement sa forme et sa hauteur — c'est le film qui
 * défile plus vite. Deux conséquences heureuses : rien ne part hors de
 * l'écran, et la population reste constante, puisqu'une émission `k` fois plus
 * dense rencontre une vie `k` fois plus courte. Les traînées s'allongent
 * d'elles-mêmes, `PARTICLE_STRETCH` suivant la vitesse.
 *
 * La poussière qui retombe sous la tête (`note-dust.ts`) ne suit pas cette
 * horloge, à dessein : elle tombe, et une chute ne dépend pas du tempo.
 */
export class HaloParticlesEffect implements NoteEffect {
  protected container: Container;
  private haloLayer: Container;
  private particleLayer: ParticleLayer;

  private haloByMidi = new Map<number, HaloState>();
  private haloPool: HaloState[] = [];

  /**
   * Les particules en vol, et celles qui attendent de resservir.
   *
   * Une particule morte quittait le tableau… jamais : elle restait dans le
   * `ParticleContainer` à alpha 0, et Pixi la renvoyait au GPU à chaque
   * image. Après une minute de jeu, mille quads morts étaient transférés et
   * rasterisés à chaque image, pour le reste de la séance. Elle sort donc du
   * conteneur à sa mort et revient par `free`, qui remplace aussi le balayage
   * linéaire qui cherchait une place libre.
   */
  private particles: ParticleState[] = [];
  private free: ParticleState[] = [];

  constructor(parent: Container) {
    this.container = new Container();

    // `vertex` porte l'échelle — la traînée s'allonge avec la vitesse, et
    // Pixi n'a pas de propriété `scale` : le nom était ignoré en silence.
    // `ParticleLayer` dit le reste : mort en temps constant, tampon statique
    // figé une fois la population au plus haut.
    this.particleLayer = new ParticleLayer(this.container, {
      position: true,
      rotation: true,
      vertex: true,
      color: true,
      uvs: false,
    });

    // No filter on this layer, and that is the point: see `halo-texture.ts`.
    // The blur it used to carry drew a rectangle around every pressed key and
    // kept the halo's additive blending inside an offscreen copy of itself.
    this.haloLayer = new Container();

    this.container.addChild(this.haloLayer);
    parent.addChild(this.container);
  }

  update(frame: NoteEffectFrame): void {
    const clock = Math.min(CLOCK_MAX, Math.max(CLOCK_MIN, frame.rollSpeedRatio));
    const rollMS = frame.deltaMS * clock;

    this.updateHalos(frame, rollMS);
    this.updateParticles(rollMS);
  }

  isAnimating(): boolean {
    return this.haloByMidi.size > 0 || this.particles.length > 0;
  }

  destroy(): void {
    for (const state of this.haloByMidi.values()) {
      disposeHalo(state);
    }

    for (const state of this.haloPool) {
      disposeHalo(state);
    }

    this.haloByMidi.clear();
    this.haloPool.length = 0;
    this.particles.length = 0;
    this.free.length = 0;
    this.container.destroy({ children: true });
  }

  // ── Halo ──────────────────────────────────────────────────────

  /**
   * `rollMS` presse l'émission avec le reste : une note qui traverse la ligne
   * deux fois plus vite crache deux fois plus d'étincelles par seconde, donc
   * autant d'étincelles au total. Le halo, lui, ne bat pas — il se redessine
   * sur la frappe.
   */
  private updateHalos(frame: NoteEffectFrame, rollMS: number): void {
    for (const state of this.haloByMidi.values()) {
      state.active = false;
    }

    for (const press of frame.presses) {
      let state = this.haloByMidi.get(press.midi);

      if (!state) {
        state = this.acquireHalo();
        state.emitAccumulatorMS = 0;
        this.haloByMidi.set(press.midi, state);
      }

      state.active = true;
      const intensity = velocityIntensity(press.velocity);
      drawHalo(state, press, HALO_BASE_RADIUS);

      // Emit rate scales with velocity (harder press → more particles per second).
      const emitInterval = PARTICLE_EMIT_INTERVAL_MS / intensity;
      state.emitAccumulatorMS += rollMS;

      while (state.emitAccumulatorMS >= emitInterval) {
        this.spawnParticle(press, intensity);
        state.emitAccumulatorMS -= emitInterval;
      }
    }

    for (const [midi, state] of this.haloByMidi) {
      if (!state.active) {
        hideHalo(state);
        this.haloByMidi.delete(midi);
        this.haloPool.push(state);
      }
    }
  }

  private acquireHalo(): HaloState {
    const reused = this.haloPool.pop();

    if (reused) {
      return reused;
    }

    return { ...createHalo(this.haloLayer), emitAccumulatorMS: 0 };
  }

  // ── Particles ─────────────────────────────────────────────────

  /** `deltaMS` est ici du temps de rouleau, pas de montre. Voir `update`. */
  private updateParticles(deltaMS: number): void {
    const dtSec = deltaMS / 1000;

    // Parcours à rebours : une particule morte est remplacée par la dernière
    // du tableau, ce qui ne décale que des indices déjà visités.
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];

      p.ageMS += deltaMS;

      if (p.ageMS >= PARTICLE_LIFETIME_MS) {
        this.retire(i);
        continue;
      }

      // Gravity pulls vy back toward zero (positive y is down).
      p.vy += PARTICLE_GRAVITY * dtSec;
      p.x += p.vx * dtSec;
      p.y += p.vy * dtSec;

      const lifeRatio = p.ageMS / PARTICLE_LIFETIME_MS;
      const alpha = (1 - lifeRatio) * (1 - lifeRatio);
      const size = p.baseSize * (1 - lifeRatio * 0.5);

      // Stretch along the velocity vector so the streak length tracks speed.
      const speed = Math.hypot(p.vx, p.vy);
      const speedRatio = Math.min(1.5, speed / PARTICLE_REF_SPEED);
      const length = size * (1 + speedRatio * PARTICLE_STRETCH);
      const thickness = size * PARTICLE_THICKNESS_FACTOR;

      // Texture.WHITE is 1×1, so scaleX/scaleY = visual width/height in pixels.
      p.particle.x = p.x;
      p.particle.y = p.y;
      p.particle.rotation = Math.atan2(p.vy, p.vx);
      p.particle.scaleX = length;
      p.particle.scaleY = thickness;
      p.particle.alpha = alpha;
    }

    this.particleLayer.flush();
  }

  private retire(index: number): void {
    const p = this.particles[index];
    const last = this.particles.length - 1;

    p.active = false;
    // Les deux tableaux vivent indice par indice : `particles[i]` dessine
    // `particleChildren[i]`.
    this.particleLayer.removeAt(index);
    this.particles[index] = this.particles[last];
    this.particles.length = last;
    this.free.push(p);
  }

  private spawnParticle(press: ActivePress, intensity: number): void {
    let state = this.free.pop();

    if (!state) {
      if (this.particles.length >= MAX_PARTICLES) {
        return;
      }

      state = this.createParticle();
    }

    this.particleLayer.add(state.particle);
    this.particles.push(state);

    const baseSize = PARTICLE_MIN_SIZE + Math.random() * (PARTICLE_MAX_SIZE - PARTICLE_MIN_SIZE);

    state.active = true;
    state.ageMS = 0;
    state.x = press.x + (Math.random() - 0.5) * press.keyWidth * 0.15;
    state.y = press.y + (Math.random() - 0.5) * 2;
    state.vx = (Math.random() - 0.5) * PARTICLE_VX_SPREAD * intensity;
    state.vy = (PARTICLE_INITIAL_VY + (Math.random() - 0.5) * PARTICLE_VY_SPREAD) * intensity;
    state.baseSize = baseSize * (0.75 + (0.25 * intensity) / 1.2);

    state.particle.tint = brightenTowardWhite(press.color, PARTICLE_WHITE_MIX);
    state.particle.alpha = 1;
    state.particle.scaleX = state.baseSize;
    state.particle.scaleY = state.baseSize;
  }

  private createParticle(): ParticleState {
    const particle = new Particle({
      texture: Texture.WHITE,
      anchorX: 0.5,
      anchorY: 0.5,
      alpha: 0,
    });

    const state: ParticleState = {
      active: false,
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      ageMS: 0,
      baseSize: PARTICLE_MIN_SIZE,
      particle,
    };

    return state;
  }
}
